import { useEffect, useState } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/firebase/app";
import { useCurrentRapidData, useIsTraining } from "@/store";
import { IMPROVE_DATA_READY_THRESHOLD } from "@/constants/inference";

/**
 * Hook to watch the review batch count in real-time using Firestore listeners.
 *
 * Subscribes directly to the datasets collection filtered by rapidDataId,
 * then to the "rapid-review" batch subcollection. Both listeners are real-time,
 * so the count updates immediately when the background process creates the
 * dataset/batch or adds images — no polling or server API calls needed.
 */
export function useReviewBatchCount() {
    const currentRapidData = useCurrentRapidData();
    const rapidDataId = currentRapidData?.id;
    const isTraining = useIsTraining();
    const [count, setCount] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const newDataReady = !isTraining && (count ?? 0) >= IMPROVE_DATA_READY_THRESHOLD;

    useEffect(() => {
        if (!rapidDataId) {
            setCount(null);
            return;
        }

        setIsLoading(true);
        let batchUnsub: (() => void) | null = null;

        // Listen for the dataset with this rapidDataId
        const datasetUnsub = onSnapshot(
            query(collection(db, "datasets"), where("rapidDataId", "==", rapidDataId)),
            (datasetSnap) => {
                if (datasetSnap.empty) {
                    // Dataset doesn't exist yet — listener stays active and will
                    // fire again when the background process creates it
                    setCount(0);
                    setIsLoading(false);
                    return;
                }

                const datasetId = datasetSnap.docs[0].id;

                // Clean up previous batch listener if dataset changed
                if (batchUnsub) {
                    batchUnsub();
                }

                // Listen for the "rapid-review" batch within this dataset
                batchUnsub = onSnapshot(
                    query(
                        collection(db, "datasets", datasetId, "batches"),
                        where("name", "==", "rapid-review")
                    ),
                    (batchSnap) => {
                        if (batchSnap.empty) {
                            setCount(0);
                        } else {
                            setCount(batchSnap.docs[0].data().images ?? 0);
                        }
                        setIsLoading(false);
                    },
                    (error) => {
                        console.warn("[useReviewBatchCount] Batch listener error:", error);
                        setIsLoading(false);
                    }
                );
            },
            (error) => {
                console.warn("[useReviewBatchCount] Dataset listener error:", error);
                setIsLoading(false);
            }
        );

        return () => {
            datasetUnsub();
            if (batchUnsub) {
                batchUnsub();
            }
        };
    }, [rapidDataId]);

    return { count, isLoading, newDataReady };
}
